import { useUrlParams } from "./useUrlParams";

/** 组织归属维度:全部 / 指定部门 / 指定成员 / 只看我的。 */
export type OrgScope = "all" | "department" | "member" | "mine";

/** 下推给 list 查询的归属参数(spread 进 request + 稳定进 queryKey)。 */
export interface OrgScopeParams {
  scope?: OrgScope;
  departmentId?: number;
  memberId?: number;
}

export interface OrgScopeFilter {
  scope: OrgScope;
  /** URL 原值(string),级联下拉 value 用。 */
  departmentId: string | null;
  memberId: string | null;
  canSwitchScope: boolean;
  scopeParams: OrgScopeParams;
  setScope: (v: string) => void;
  setDepartmentId: (v: string) => void;
  setMemberId: (v: string) => void;
  /** 原子选择(树 picker 用):一次 URL update 同时设 scope + 归属,避免 setScope→setDeptId 两步竞态。 */
  selectAll: () => void;
  selectMine: () => void;
  selectDepartment: (departmentId: string) => void;
  selectMember: (departmentId: string, memberId: string) => void;
}

function normalizeScope(raw: string | null): OrgScope {
  return raw === "department" || raw === "member" || raw === "mine" ? raw : "all";
}

/**
 * 组织级归属筛选的 URL 状态 + RBAC 钳制(纯 hook,无 orgService/useAuth 依赖 —— `canSwitchScope`
 * 由页面从 `isTenantManagerPlus(user?.role)` 传入,保持 shared 层纯净)。
 *
 * <p>逻辑源自 PosterDashboardPage 手搓版(#9):
 * - 切归属维度清掉部门/成员/页码(useUrlParams synchronous-ref-advance 已根治同 handler 多次 update 互相覆盖);
 * - STAFF(canSwitchScope=false)下发 `scope` 但**不带部门/成员 id**(那两个是管理维度,
 *   STAFF 无 UI 也无权限指定别人)。★此前 STAFF 返回空对象,注释断言「后端强制 own」——
 *   对海报/视频/分身成立(MediaTaskService 钳 ownerUserId),对新闻线**不成立**
 *   (reporter listByTenant 是租户级轻列表,无 own 强制)→ STAFF 选了「只看我的」也传不下去
 *   (E2E 0810 RP-WK-01)。传 scope 对前三线语义等价(后端照样钳制),对新闻线才让 mine 真生效;
 * - MANAGER+ 一律下发 `scope`(含 "all" = 授权天花板),后端 `resolveOwnerFilter` 钳制在授权作用域内。
 *
 * @param opts.canSwitchScope MANAGER+ 才可切维度(STAFF 后端强制隔离)。
 * @param opts.resetKeys 切换归属时额外清空的 URL 键(默认 `["page"]`,回到首页)。
 */
export function useOrgScopeFilter(opts: {
  canSwitchScope: boolean;
  resetKeys?: string[];
}): OrgScopeFilter {
  const { canSwitchScope, resetKeys = ["page"] } = opts;
  const { sp, update } = useUrlParams();

  const scope = normalizeScope(sp.get("scope"));
  const departmentId = sp.get("departmentId");
  const memberId = sp.get("memberId");

  const nulledResetKeys = () => Object.fromEntries(resetKeys.map((k) => [k, null] as const));

  const setScope = (v: string) => {
    update({
      scope: v === "all" ? null : v,
      departmentId: null,
      memberId: null,
      ...nulledResetKeys(),
    });
  };
  const setDepartmentId = (v: string) => {
    update({ departmentId: v || null, memberId: null, ...nulledResetKeys() });
  };
  const setMemberId = (v: string) => {
    update({ memberId: v || null, ...nulledResetKeys() });
  };

  // 原子选择:一次 update 同时定 scope + 归属(树 picker 点节点即选定,无两步竞态)。
  const selectAll = () =>
    update({ scope: null, departmentId: null, memberId: null, ...nulledResetKeys() });
  const selectMine = () =>
    update({ scope: "mine", departmentId: null, memberId: null, ...nulledResetKeys() });
  const selectDepartment = (id: string) =>
    update({ scope: "department", departmentId: id, memberId: null, ...nulledResetKeys() });
  const selectMember = (deptId: string, mId: string) =>
    update({ scope: "member", departmentId: deptId, memberId: mId, ...nulledResetKeys() });

  // ★STAFF 默认仍是 "all" 而不是钉死 "mine":selectAll 会把 URL 的 scope 键整个清掉,
  //   若默认落 mine,「只看我的」开关就永远关不掉(关=清键=又回默认 mine)。且新闻舱内
  //   列表本就是租户级共享,/works 默认与舱内一致、开关给用户显式收窄 —— 行为变化最小。
  const scopeParams: OrgScopeParams = canSwitchScope
    ? {
        scope,
        ...(departmentId ? { departmentId: Number(departmentId) } : {}),
        ...(memberId ? { memberId: Number(memberId) } : {}),
      }
    : { scope };

  return {
    scope,
    departmentId,
    memberId,
    canSwitchScope,
    scopeParams,
    setScope,
    setDepartmentId,
    setMemberId,
    selectAll,
    selectMine,
    selectDepartment,
    selectMember,
  };
}
